import { create } from 'zustand';
import { toast } from 'react-toastify';

// Full 6-tier plan catalog matching the QuantrolixAI pricing structure
const SUBSCRIPTION_PLANS = [
  {
    key: 'quantum_spark',
    name: 'Quantum Spark',
    price: 29,
    annualPrice: 23,
    scans: 1500,
    apiCalls: 10000,
    marketplaces: 10,
    teamMembers: 1,
    dataRetentionDays: 30,
    apps: 2,
    description: 'Perfect for solo sellers getting started with marketplace intelligence.',
    features: [
      '1,500 Product Scans/mo',
      '10,000 API Calls/mo',
      '10 Marketplaces',
      '2 App Access',
      '30-Day Data Retention',
      '1 User Seat',
      'Email Support',
      'Basic Alerts',
    ],
    cta: 'Get Started',
    color: '#00d4ff',
  },
  {
    key: 'neural_sync',
    name: 'Neural Sync',
    price: 49,
    annualPrice: 39,
    scans: 5000,
    apiCalls: 50000,
    marketplaces: 25,
    teamMembers: 2,
    dataRetentionDays: 90,
    apps: 5,
    isPopular: true,
    description: 'Ideal for growing sellers who need deeper market insights.',
    features: [
      '5,000 Product Scans/mo',
      '50,000 API Calls/mo',
      '25 Marketplaces',
      '5 App Access',
      '90-Day Data Retention',
      '2 User Seats',
      'Priority Email Support',
      'Real-Time Alerts',
      'Basic Analytics',
    ],
    cta: 'Start Neural Sync',
    color: '#9333ea',
  },
  {
    key: 'quantum_pro',
    name: 'Quantum Pro',
    price: 99,
    annualPrice: 79,
    scans: 15000,
    apiCalls: 150000,
    marketplaces: 50,
    teamMembers: 5,
    dataRetentionDays: 180,
    apps: -1,
    description: 'Full-featured plan for professional sellers and small teams.',
    features: [
      '15,000 Product Scans/mo',
      '150,000 API Calls/mo',
      '50 Marketplaces',
      'All Apps Included',
      '180-Day Data Retention',
      'Up to 5 Users',
      'Priority Support',
      'Advanced Analytics',
      'API Access',
      'Custom Dashboards',
    ],
    cta: 'Go Pro',
    color: '#667eea',
  },
  {
    key: 'singularity',
    name: 'Singularity',
    price: 199,
    annualPrice: 159,
    scans: 50000,
    apiCalls: 500000,
    marketplaces: 83,
    teamMembers: 15,
    dataRetentionDays: 365,
    apps: -1,
    description: 'Enterprise-grade intelligence for high-volume operations.',
    features: [
      '50,000 Product Scans/mo',
      '500,000 API Calls/mo',
      'All 83+ Marketplaces',
      'All Apps Included',
      '1-Year Data Retention',
      'Up to 15 Users',
      'Dedicated Support Rep',
      'Advanced Analytics',
      'Full API Access',
      'Custom Dashboards',
      'Webhook Integrations',
    ],
    cta: 'Upgrade to Singularity',
    color: '#f59e0b',
  },
  {
    key: 'infinity_pro',
    name: 'Infinity Pro',
    price: 399,
    annualPrice: 319,
    scans: 150000,
    apiCalls: 1500000,
    marketplaces: 100,
    teamMembers: 50,
    dataRetentionDays: -1,
    apps: -1,
    description: 'Unlimited power for large-scale marketplace dominance.',
    features: [
      '150,000 Product Scans/mo',
      '1,500,000 API Calls/mo',
      'All Marketplaces + Beta',
      'All Apps Included',
      'Unlimited Data Retention',
      'Up to 50 Users',
      'Dedicated Account Manager',
      'White-Glove Onboarding',
      'Full API Access',
      'Custom Integrations',
      'SLA Guarantee (99.9%)',
    ],
    cta: 'Go Infinity',
    color: '#ec4899',
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: 999,
    annualPrice: 799,
    scans: -1,
    apiCalls: -1,
    marketplaces: 100,
    teamMembers: -1,
    dataRetentionDays: -1,
    apps: -1,
    description: 'Custom solutions with dedicated infrastructure for the largest operations.',
    features: [
      'Unlimited Product Scans',
      'Unlimited API Calls',
      'All Marketplaces + Beta',
      'All Apps Included',
      'Unlimited Data Retention',
      'Unlimited Users',
      'Dedicated Account Manager',
      'Custom Development',
      'SLA Guarantee (99.99%)',
      'SSO / SAML Auth',
      'On-Premise Option',
      'Custom Integrations',
    ],
    cta: 'Contact Sales',
    color: '#14b8a6',
  },
];

// Mock invoice data
const MOCK_INVOICES = [
  {
    id: 'INV-2026-0201',
    date: '2026-02-01',
    amount: 99.00,
    status: 'paid',
    description: 'Quantum Pro - Monthly Subscription',
    pdfUrl: '#',
  },
  {
    id: 'INV-2026-0101',
    date: '2026-01-01',
    amount: 99.00,
    status: 'paid',
    description: 'Quantum Pro - Monthly Subscription',
    pdfUrl: '#',
  },
  {
    id: 'INV-2025-1201',
    date: '2025-12-01',
    amount: 99.00,
    status: 'paid',
    description: 'Quantum Pro - Monthly Subscription',
    pdfUrl: '#',
  },
  {
    id: 'INV-2025-1101',
    date: '2025-11-01',
    amount: 49.00,
    status: 'paid',
    description: 'Neural Sync - Monthly Subscription',
    pdfUrl: '#',
  },
  {
    id: 'INV-2025-1001',
    date: '2025-10-01',
    amount: 49.00,
    status: 'paid',
    description: 'Neural Sync - Monthly Subscription',
    pdfUrl: '#',
  },
  {
    id: 'INV-2025-0901',
    date: '2025-09-01',
    amount: 49.00,
    status: 'paid',
    description: 'Neural Sync - Monthly Subscription',
    pdfUrl: '#',
  },
  {
    id: 'INV-2025-0801',
    date: '2025-08-01',
    amount: 29.00,
    status: 'paid',
    description: 'Quantum Spark - Monthly Subscription',
    pdfUrl: '#',
  },
  {
    id: 'INV-2025-0701',
    date: '2025-07-01',
    amount: 29.00,
    status: 'refunded',
    description: 'Quantum Spark - Monthly Subscription',
    pdfUrl: '#',
  },
];

const useSubscriptionStore = create((set, get) => ({
  // Current subscription state
  currentPlan: 'quantum_pro',
  subscriptionStatus: 'active', // active, paused, cancelled, past_due, trialing
  renewalDate: '2026-03-01',
  billingCycle: 'monthly', // monthly, annual
  subscribedSince: '2025-07-15',

  // Payment method
  paymentMethod: {
    type: 'visa',
    last4: '4242',
    expMonth: 12,
    expYear: 2027,
    brand: 'Visa',
    isDefault: true,
  },

  // Usage data
  usageData: {
    scans: {
      used: 8420,
      limit: 15000,
      resetDate: '2026-03-01',
    },
    apiCalls: {
      used: 87350,
      limit: 150000,
      resetDate: '2026-03-01',
    },
    storage: {
      used: 2.4,
      limit: 10,
      unit: 'GB',
    },
    teamMembers: {
      used: 3,
      limit: 5,
    },
  },

  // Invoices
  invoices: MOCK_INVOICES,

  // Plan catalog
  plans: SUBSCRIPTION_PLANS,

  // UI state
  isChangingPlan: false,
  isCancelFlowOpen: false,
  cancelStep: 1, // 1: reason, 2: retention offer, 3: confirm

  // Helpers
  getCurrentPlanConfig: () => {
    const { currentPlan, plans } = get();
    return plans.find((p) => p.key === currentPlan) || plans[0];
  },

  getUsagePercentage: (type) => {
    const { usageData } = get();
    const data = usageData[type];
    if (!data || data.limit <= 0 || data.limit === -1) return 0;
    return Math.min((data.used / data.limit) * 100, 100);
  },

  getUsageStatus: (type) => {
    const percentage = get().getUsagePercentage(type);
    if (percentage >= 100) return 'exceeded';
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning';
    return 'normal';
  },

  // Actions
  changePlan: async (planKey) => {
    const { plans, currentPlan } = get();
    const targetPlan = plans.find((p) => p.key === planKey);
    if (!targetPlan) return;

    if (planKey === 'enterprise') {
      toast.info('Our team will contact you within 24 hours. Email sales@quantrolix.ai for immediate assistance.');
      return;
    }

    if (planKey === currentPlan) {
      toast.info('You are already on this plan.');
      return;
    }

    set({ isChangingPlan: true });

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const currentPlanConfig = get().getCurrentPlanConfig();
    const isUpgrade = targetPlan.price > currentPlanConfig.price;

    set({
      currentPlan: planKey,
      isChangingPlan: false,
      subscriptionStatus: 'active',
      usageData: {
        ...get().usageData,
        scans: {
          ...get().usageData.scans,
          limit: targetPlan.scans === -1 ? Infinity : targetPlan.scans,
        },
        apiCalls: {
          ...get().usageData.apiCalls,
          limit: targetPlan.apiCalls === -1 ? Infinity : targetPlan.apiCalls,
        },
        teamMembers: {
          ...get().usageData.teamMembers,
          limit: targetPlan.teamMembers === -1 ? Infinity : targetPlan.teamMembers,
        },
      },
    });

    if (isUpgrade) {
      toast.success(`Upgraded to ${targetPlan.name}! Your new features are available immediately.`);
    } else {
      toast.success(`Switched to ${targetPlan.name}. Changes take effect at the start of your next billing cycle.`);
    }
  },

  updatePaymentMethod: async (newMethod) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    set({
      paymentMethod: {
        ...get().paymentMethod,
        ...newMethod,
      },
    });

    toast.success('Payment method updated successfully.');
  },

  cancelSubscription: async (reason) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    set({
      subscriptionStatus: 'cancelled',
      isCancelFlowOpen: false,
      cancelStep: 1,
    });

    toast.success('Your subscription has been cancelled. You will retain access until the end of your current billing period.');
  },

  pauseSubscription: async (months) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    set({
      subscriptionStatus: 'paused',
      isCancelFlowOpen: false,
      cancelStep: 1,
    });

    toast.success(`Subscription paused for ${months} month${months > 1 ? 's' : ''}. We will resume billing automatically.`);
  },

  resumeSubscription: async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    set({
      subscriptionStatus: 'active',
    });

    toast.success('Subscription resumed! Welcome back.');
  },

  downloadInvoice: async (invoiceId) => {
    // Simulate PDF download
    toast.info(`Downloading invoice ${invoiceId}...`);
    await new Promise((resolve) => setTimeout(resolve, 800));
    toast.success(`Invoice ${invoiceId} downloaded.`);
  },

  // UI state management
  openCancelFlow: () => set({ isCancelFlowOpen: true, cancelStep: 1 }),
  closeCancelFlow: () => set({ isCancelFlowOpen: false, cancelStep: 1 }),
  setCancelStep: (step) => set({ cancelStep: step }),
  setBillingCycle: (cycle) => set({ billingCycle: cycle }),
}));

export default useSubscriptionStore;
